import { useNavigate } from 'react-router';
import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { AuthHeading } from '@/bundle/Auth/ui/AuthHeading/AuthHeading';
import { Box } from '@/components/Box/Box';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  confirmInternalOrganizationMasterServiceAgreement,
  getInternalOrganizationMasterServiceAgreement,
} from '@/bundle/Auth/AdminOnboardingFlow/AcceptMasterServiceAgreementPage/api';
import { formatDate, formatDateTimeUTC, FULL_MONTH_DATE_FORMAT } from '@/helpers/dateHelpers/dateHelpers';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Notification } from '@/components/Notification/Notification';
import { AcceptMasterServiceAgreementWidgetWrapper } from '@/bundle/Auth/AdminOnboardingFlow/AcceptMasterServiceAgreementPage/ui/AcceptMasterServiceAgreementWidgetWrapper';
import { AdminOnboardingStepper } from '../ui/AdminOnboardingStepper';
import { useUser } from '@/context/userContext';
import { ConfirmInternalOrganizationMasterServiceAgreementPayloadType } from '@/api/v1/organization/internal/confirmInternalOrganizationMasterServiceAgreementApi';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { getSelectBillingPlanPath } from '../SelectBillingPlanPage/path';
import { AcceptMasterServiceAgreementFormValuesType } from './ui/AcceptMasterServiceAgreementForm';
import { getAdminOnboardingFlow } from '@/bundle/shared/hooks/useAdminOnboardingFlowFromAuthToken';

export const AcceptMasterServiceAgreementPage = () => {
  const navigate = useNavigate();
  const { user, refetchUser } = useUser();

  const { data, isPending } = useQuery({
    queryKey: ['get_internal_organization_master_service_agreement'],
    queryFn() {
      return getInternalOrganizationMasterServiceAgreement();
    },
  });

  const masterServiceAgreement = data?.body;
  const modifiedDate = formatDate(masterServiceAgreement?.date_version, FULL_MONTH_DATE_FORMAT);

  const { mutate, isPending: isPendingConfirmMasterServiceAgreement } = useMutation({
    mutationKey: ['confirm_master_service_agreement'],
    mutationFn: (values: AcceptMasterServiceAgreementFormValuesType) => {
      const payload: ConfirmInternalOrganizationMasterServiceAgreementPayloadType = {
        ...values,
        agreement_id: masterServiceAgreement?.id,
      };

      return confirmInternalOrganizationMasterServiceAgreement(payload);
    },
    async onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      const currentDate = new Date().toISOString();
      const currentFormattedDate = formatDateTimeUTC(currentDate);

      showSnackbar(
        <Box display='flex' flexDirection='column' rowGap='2px'>
          <Typography variant='caption' color={COLORS.grey[200]} fontWeight='medium'>
            Thank you. You have successfully signed the Master Service Agreement.
          </Typography>
          <Typography variant='captionSmall' color={COLORS.grey[200]} fontWeight='medium'>
            {`(Last modified: ${modifiedDate}) on ${currentFormattedDate} UTC. A record of your acceptance has been securely stored.`}
          </Typography>
        </Box>
      );

      amplitudeService.logEvent(AMPLITUDE_EVENTS.AcceptMasterServiceAgreementSuccess);
      await refetchUser();

      return navigate(getSelectBillingPlanPath());
    },
  });

  const { isOnboardingFlow, signUpStep } = getAdminOnboardingFlow(user);
  const error = getResponseError(data?.error);

  return (
    <AuthLayout
      position='top'
      isLoading={isPending}
      banner={isOnboardingFlow && <AdminOnboardingStepper signUpStep={signUpStep} />}
    >
      <AuthHeading
        header='Master Services Agreement'
        subHeader={
          <Box display='flex' flexDirection='column'>
            Before proceeding, please review and accept our Master Service Agreement.
            <br />
            By signing electronically, you acknowledge that you have read, understood, and agree to be bound by the
            terms of this agreement.
          </Box>
        }
      />

      {error && <Notification variant='error'>{error}</Notification>}

      {masterServiceAgreement && (
        <Box marginBottom='32px'>
          <AcceptMasterServiceAgreementWidgetWrapper
            agreementDocument={masterServiceAgreement}
            modifiedDate={modifiedDate}
            isLoading={isPendingConfirmMasterServiceAgreement}
            onConfirm={mutate}
          />
        </Box>
      )}
    </AuthLayout>
  );
};
